/**
 * Polls the Gamepad API each frame and fires edge-triggered callbacks.
 * Supports the W3C "standard" gamepad mapping.
 *
 * Call `poll()` once per animation frame (e.g. at the start of the game-loop
 * update).  Callbacks fire synchronously during `poll()`.
 *
 * Standard button indices (https://w3c.github.io/gamepad/#remapping):
 *   0  A / Cross        — primary action (grapple)
 *   1  B / Circle
 *   2  X / Square
 *   3  Y / Triangle
 *   4  LB / L1
 *   5  RB / R1
 *   6  LT / L2
 *   7  RT / R2
 *   8  Back / Select / View  — restart
 *   9  Start / Menu / Options — pause
 *  12  D-pad Up
 *  13  D-pad Down
 *  14  D-pad Left
 *  15  D-pad Right
 */

export interface GamepadCallbacks {
  /** Fired once when a button transitions from released → pressed. */
  onButtonDown: (button: number) => void;
  /** Fired once when a button transitions from pressed → released. */
  onButtonUp: (button: number) => void;
}

export class GamepadInput {
  private prevPressed: Map<number, boolean[]> = new Map();

  constructor(private readonly cb: GamepadCallbacks) {
    window.addEventListener('gamepadconnected', this.handleConnected);
    window.addEventListener('gamepaddisconnected', this.handleDisconnected);
  }

  private handleConnected = (e: GamepadEvent): void => {
    this.prevPressed.set(
      e.gamepad.index,
      new Array(e.gamepad.buttons.length).fill(false),
    );
  };

  private handleDisconnected = (e: GamepadEvent): void => {
    this.prevPressed.delete(e.gamepad.index);
  };

  /**
   * Must be called once per rendered frame.  Reads the current state of every
   * connected gamepad and fires `onButtonDown` / `onButtonUp` for any edges
   * detected since the previous call.
   */
  poll(): void {
    const pads = navigator.getGamepads();
    for (const pad of pads) {
      if (!pad) continue;
      let prev = this.prevPressed.get(pad.index);
      if (!prev) {
        prev = new Array(pad.buttons.length).fill(false);
        this.prevPressed.set(pad.index, prev);
      }
      for (let i = 0; i < pad.buttons.length; i++) {
        const cur = pad.buttons[i].pressed;
        if (cur && !prev[i]) this.cb.onButtonDown(i);
        else if (!cur && prev[i]) this.cb.onButtonUp(i);
        prev[i] = cur;
      }
    }
  }

  destroy(): void {
    window.removeEventListener('gamepadconnected', this.handleConnected);
    window.removeEventListener('gamepaddisconnected', this.handleDisconnected);
    this.prevPressed.clear();
  }
}

/** Standard gamepad button indices for readable code at call sites. */
export const GamepadButton = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  BACK: 8,
  START: 9,
  L3: 10,
  R3: 11,
  DPAD_UP: 12,
  DPAD_DOWN: 13,
  DPAD_LEFT: 14,
  DPAD_RIGHT: 15,
} as const;
